/**
 * Cookie utility functions for managing browser cookies
 */

/**
 * Set a cookie with the given name, value, and expiration days
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Number of days until expiration (default: 30)
 * @param path - Cookie path (default: '/')
 */
export function setCookie(name: string, value: string, days: number = 30, path: string = '/'): void {
  try {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    const encodedValue = encodeURIComponent(value);
    document.cookie = `${name}=${encodedValue};${expires};path=${path};SameSite=Lax`;
  } catch (error) {
    console.error('Error setting cookie:', error);
  }
}

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  try {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        const value = c.substring(nameEQ.length, c.length);
        return decodeURIComponent(value);
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting cookie:', error);
    return null;
  }
}

/**
 * Check if a cookie exists
 * @param name - Cookie name
 * @returns True if cookie exists, false otherwise
 */
export function cookieExists(name: string): boolean {
  return getCookie(name) !== null;
}

/**
 * Delete a cookie by setting its expiration to the past
 * @param name - Cookie name
 * @param path - Cookie path (default: '/')
 */
export function deleteCookie(name: string, path: string = '/'): void {
  try {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path};`;
  } catch (error) {
    console.error('Error deleting cookie:', error);
  }
}

/**
 * Get all cookies as an object
 * @returns Object with cookie names as keys and values as values
 */
export function getAllCookies(): Record<string, string> {
  try {
    const cookies: Record<string, string> = {};
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      const eqIndex = c.indexOf('=');
      if (eqIndex > 0) {
        const name = c.substring(0, eqIndex);
        const value = c.substring(eqIndex + 1);
        cookies[name] = decodeURIComponent(value);
      }
    }
    return cookies;
  } catch (error) {
    console.error('Error getting all cookies:', error);
    return {};
  }
}

/**
 * Store URL tracking parameters in cookies
 * @param searchParams - URLSearchParams object
 * @param expirationDays - Number of days until expiration (default: 30)
 */
const TRACKED_QUERY_PARAMS_COOKIE = 'tracked_query_params';

export function storeTrackingParamsInCookies(searchParams: URLSearchParams, expirationDays: number = 30): void {
  try {
    const trackedKeys: Set<string> = new Set();

    // Merge with any previously tracked keys
    const existing = getCookie(TRACKED_QUERY_PARAMS_COOKIE);
    trackedKeys.add("rewardful_affiliate");
    if (existing) {
      try {
        const parsed = JSON.parse(existing) as string[];
        parsed.forEach(k => trackedKeys.add(k));
      } catch {}
    }

    // Check for fbclid and generate _fbc cookie if needed
    const fbclid = searchParams.get('fbclid');
    if (fbclid && !getCookie('_fbc')) {
      const fbcValue = generateFbcValue(fbclid);
      if (fbcValue) {
        setCookie('_fbc', fbcValue, expirationDays);
        trackedKeys.add('_fbc');
      }
    }

    // Store all query params present in URL
    searchParams.forEach((value, key) => {
      if (value != null && value !== '') {
        setCookie(key, value, expirationDays);
        trackedKeys.add(key);
      }
    });

    // Persist keys list for later retrieval
    setCookie(TRACKED_QUERY_PARAMS_COOKIE, JSON.stringify(Array.from(trackedKeys)), expirationDays);
  } catch (err) {
    console.error('Error storing tracking params in cookies:', err);
  }
}

/**
 * Generate _fbc cookie value from fbclid parameter
 * Format: fb.subdomainIndex.creationTime.fbclid
 */
function generateFbcValue(fbclid: string): string | null {
  try {
    if (!fbclid) return null;

    // Get current domain to determine subdomain index
    const hostname = window.location.hostname;
    let subdomainIndex = 0; // Default for .com domains

    if (hostname.includes('.')) {
      const parts = hostname.split('.');
      if (parts.length >= 2) {
        subdomainIndex = parts.length - 1;
      }
    }

    // Use current timestamp as creation time
    const creationTime = Date.now();

    // Format: fb.subdomainIndex.creationTime.fbclid
    const fbcValue = `fb.${subdomainIndex}.${creationTime}.${fbclid}`;

    return fbcValue;
  } catch (error) {
    console.error('Error generating _fbc value:');
    return null;
  }
}

/**
 * Store Rewardful affiliate data to cookies
 * @param expirationDays - Number of days until expiration (default: 30)
 * @returns True if affiliate data was stored, false otherwise
 */
export function storeRewardfulAffiliateToCookie(expirationDays: number = 30): boolean {
  try {
    // Check if window.Rewardful exists
    if (typeof window === 'undefined' || !(window as any).Rewardful) {
      console.log('[Rewardful] window.Rewardful not available yet');
      return false;
    }

    const rewardful = (window as any).Rewardful;

    // Store only affiliate and referral data (exclude internal Rewardful properties)
    if (rewardful.affiliate || rewardful.referral) {
      // Create an object with only affiliate and referral data
      const rewardfulData: any = {};

      if (rewardful.affiliate) {
        rewardfulData.affiliate = rewardful.affiliate;
      }

      if (rewardful.referral) {
        rewardfulData.referral = rewardful.referral;
      }

      // Store as JSON string
      const jsonString = JSON.stringify(rewardfulData);
      setCookie('rewardful_affiliate', jsonString, expirationDays);
      console.log('[Rewardful] Stored affiliate data to cookie:', rewardfulData);
      return true;
    }

    console.log('[Rewardful] No affiliate or referral data found');
    return false;
  } catch (error) {
    console.error('[Rewardful] Error storing affiliate to cookie:', error);
    return false;
  }
}

/**
 * Parse cookie value - handles both strings and JSON objects
 * @param value - Cookie value to parse
 * @returns Parsed value (string or object)
 */
function parseCookieValue(value: string): string | object {
  try {
    // Try to parse as JSON first
    const parsed = JSON.parse(value);
    // If it's an object or array, return it
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed;
    }
    // If it's a primitive, return the original string
    return value;
  } catch {
    // If JSON parsing fails, return as string
    return value;
  }
}

/**
 * Get ads metadata from cookies for API requests
 * @returns Object containing ads metadata from cookies (values can be strings or objects)
 */
export function getAdsMetadataFromCookies(): Record<string, string | object> {
  const adsMetadata: Record<string, string | object> = {};

  // Backend-set attribution cookies to always include if present
  const ALWAYS_INCLUDE_COOKIES = ['_fbp', '_fcp', '_ttp', '_fbc'];

  // Prefer the explicit list of keys we tracked from query params
  const tracked = getCookie(TRACKED_QUERY_PARAMS_COOKIE);
  if (tracked) {
    try {
      const keys = JSON.parse(tracked) as string[];
      keys.forEach((key) => {
        const value = getCookie(key);
        if (value) {
          // Always parse cookie values (including rewardful_affiliate which is JSON)
          adsMetadata[key] = parseCookieValue(value);
        }
      });
      // Always include backend-set attribution cookies if present
      ALWAYS_INCLUDE_COOKIES.forEach((key) => {
        const v = getCookie(key);
        if (v) {
          // Remove underscore prefix for backend
          const backendKey = key.startsWith('_') ? key.substring(1) : key;
          adsMetadata[backendKey] = parseCookieValue(v);
        }
      });
      return adsMetadata;
    } catch {}
  }

  // Fallback: include all non-sensitive cookies
  const all = getAllCookies();
  Object.entries(all).forEach(([key, value]) => {
    const lowered = key.toLowerCase();
    const isSensitive = lowered.includes('session') || lowered.includes('token') || lowered.startsWith('sb-');
    if (!isSensitive) {
      // Always parse cookie values (including rewardful_affiliate which is JSON)
      adsMetadata[key] = parseCookieValue(value);
    }
  });

  // Ensure backend-set cookies present if set by backend
  ALWAYS_INCLUDE_COOKIES.forEach((key) => {
    const v = getCookie(key);
    if (v) {
      // Remove underscore prefix for backend
      const backendKey = key.startsWith('_') ? key.substring(1) : key;
      adsMetadata[backendKey] = parseCookieValue(v);
    }
  });

  return adsMetadata;
}
